import type { AttributionData } from "@fluid-app/api-client";
import type { StorageManager } from "@fluid-app/storage-manager";
import { COOKIE_KEYS, STORAGE_KEYS } from "@fluid-app/storage-manager";

import { debug } from "../utils/debug";

// ServerAttributionData is now the same as AttributionData from API client

/**
 * Interface for attribution override configuration
 * This allows setting attribution that overrides server-calculated attribution
 */
export interface AttributionOverride {
  attribution?: AttributionData;
}

/**
 * Attribution manager that handles attribution from settings API
 * and supports attribution overrides with priority handling
 */
export class AttributionManager {
  private storageManager: StorageManager;
  private urlAttributionCache = new Map<string, AttributionData | null>();
  private static TTL = 60 * 60 * 1000; // 1 hour in ms

  constructor(storageManager: StorageManager) {
    this.storageManager = storageManager;
  }

  // Helper to generate a cache key for a URL
  private getCacheKey(url: string): string {
    try {
      const parsedUrl = new URL(url);
      return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    } catch {
      return url;
    }
  }

  // Get the persistent cache object
  private getPersistentCache(): Record<
    string,
    { attribution: AttributionData; timestamp: number; url: string }
  > {
    return (
      this.storageManager.getObject({ key: STORAGE_KEYS.ATTRIBUTION_CACHE }) ??
      {}
    );
  }

  // Save the persistent cache object
  private setPersistentCache(
    cache: Record<
      string,
      { attribution: AttributionData; timestamp: number; url: string }
    >,
  ): void {
    this.storageManager.setObject({
      key: STORAGE_KEYS.ATTRIBUTION_CACHE,
      value: cache,
    });
  }

  // Check if a cached entry is still valid
  private isCacheValid(entry: {
    attribution: AttributionData;
    timestamp: number;
    url: string;
  }): boolean {
    return Date.now() - entry.timestamp < AttributionManager.TTL;
  }

  /**
   * Store attribution data from settings API response
   * @param url The URL this attribution data is for
   * @param attribution Attribution data from settings API (API client format)
   */
  public storeServerAttribution(
    url: string,
    attribution: AttributionData | null,
  ): void {
    debug(
      "AttributionManager: Storing server attribution for URL:",
      url,
      attribution,
    );

    this.urlAttributionCache.set(url, attribution);
    const cacheKey = this.getCacheKey(url);
    const cache = this.getPersistentCache();
    if (attribution) {
      cache[cacheKey] = {
        attribution,
        timestamp: Date.now(),
        url,
      };
    } else {
      delete cache[cacheKey];
    }
    this.setPersistentCache(cache);
  }

  /**
   * Get effective attribution data respecting priority:
   * 1. Attribution override (highest priority)
   * 2. Server-calculated attribution
   * 3. Fallback (null)
   */
  public getEffectiveAttribution({
    url,
    useCachedAttribution = false,
  }: {
    url?: string;
    useCachedAttribution?: boolean;
  } = {}): AttributionData | null {
    debug("AttributionManager: Getting effective attribution for URL:", url);

    // 1. Check for attribution override (highest priority)
    const attributionOverride = this.getAttributionOverride();
    if (attributionOverride) {
      debug(
        "AttributionManager: Using attribution override:",
        attributionOverride,
      );
      return attributionOverride;
    }

    // 2. Check for server-calculated attribution
    const serverAttribution = this.getServerAttribution(url);
    if (serverAttribution) {
      debug(
        "AttributionManager: Using server-calculated attribution:",
        serverAttribution,
      );
      return serverAttribution;
    }

    if (useCachedAttribution) {
      // 3. Check for affiliate cookie
      const affiliate = this.storageManager.getObject<{
        fluid_rep_id?: number;
      }>({
        key: COOKIE_KEYS.AFFILIATE,
      });

      if (affiliate?.fluid_rep_id) {
        debug("AttributionManager: Using affiliate:", affiliate.fluid_rep_id);
        return {
          fluid_rep_id: affiliate.fluid_rep_id,
        };
      }
    }

    // 4. No attribution available
    debug("AttributionManager: No attribution available");
    return null;
  }

  /**
   * Get server-calculated attribution for the current or specified URL
   */
  public getServerAttribution(url?: string): AttributionData | null {
    const currentUrl =
      url ?? (typeof window !== "undefined" ? window.location.pathname : "");

    // Check in-memory cache first
    const cached = this.urlAttributionCache.get(currentUrl);
    if (cached !== undefined) {
      return cached;
    }

    // Check persistent cache
    const cacheKey = this.getCacheKey(currentUrl);
    const cache = this.getPersistentCache();
    const entry = cache[cacheKey];
    if (entry && this.isCacheValid(entry)) {
      // Update in-memory cache for future fast access
      this.urlAttributionCache.set(currentUrl, entry.attribution);
      return entry.attribution;
    } else if (entry) {
      // Expired, remove from persistent cache
      delete cache[cacheKey];
      this.setPersistentCache(cache);
    }
    return null;
  }

  /**
   * Set attribution override that overrides server-calculated attribution
   */
  public setAttributionOverride(
    attributionOverride: AttributionOverride,
  ): void {
    debug("Setting attribution override:", attributionOverride);
    const storedOverrides =
      this.storageManager.getObject<{
        attributionOverride?: AttributionOverride;
      }>({ key: STORAGE_KEYS.SETTINGS_OVERRIDE }) ?? {};
    storedOverrides.attributionOverride = attributionOverride;
    this.storageManager.setObject({
      key: STORAGE_KEYS.SETTINGS_OVERRIDE,
      value: storedOverrides,
    });
  }

  /**
   * Clear attribution override, falling back to server-calculated attribution
   */
  public clearAttributionOverride(): void {
    debug("Clearing attribution override");
    const storedOverrides =
      this.storageManager.getObject<{
        attributionOverride?: AttributionOverride;
      }>({ key: STORAGE_KEYS.SETTINGS_OVERRIDE }) ?? {};
    delete storedOverrides.attributionOverride;
    this.storageManager.setObject({
      key: STORAGE_KEYS.SETTINGS_OVERRIDE,
      value: storedOverrides,
    });
  }

  /**
   * Get attribution override if set
   */
  public getAttributionOverride(): AttributionData | null {
    const storedOverrides = this.storageManager.getObject<{
      attributionOverride?: AttributionOverride;
    }>({ key: STORAGE_KEYS.SETTINGS_OVERRIDE });

    if (storedOverrides?.attributionOverride?.attribution) {
      return storedOverrides.attributionOverride.attribution;
    }

    return null;
  }

  /**
   * Check if attribution data needs refresh for the current URL
   */
  public needsRefresh(url: string): boolean {
    // Needs refresh if not in memory or persistent cache, or if persistent cache is expired
    if (this.urlAttributionCache.has(url)) {
      return false;
    }
    const cacheKey = this.getCacheKey(url);
    const cache = this.getPersistentCache();
    const entry = cache[cacheKey];
    return !entry || !this.isCacheValid(entry);
  }

  /**
   * Clear attribution data for a specific URL or all URLs
   */
  public clearAttribution(url?: string): void {
    if (url) {
      this.urlAttributionCache.delete(url);
      const cacheKey = this.getCacheKey(url);
      const cache = this.getPersistentCache();
      delete cache[cacheKey];
      this.setPersistentCache(cache);
    } else {
      this.urlAttributionCache.clear();
      this.storageManager.removeItem({ key: STORAGE_KEYS.ATTRIBUTION_CACHE });
    }
  }

  /**
   * Get attribution source information for debugging
   */
  public getAttributionSource():
    | "attribution-override"
    | "server"
    | "none"
    | "cookie" {
    if (this.getAttributionOverride()) {
      return "attribution-override";
    }
    if (this.getServerAttribution()) {
      return "server";
    }
    const affiliate = this.storageManager.getObject<{ fluid_rep_id?: number }>({
      key: COOKIE_KEYS.AFFILIATE,
    });
    if (affiliate?.fluid_rep_id) {
      return "cookie";
    }
    return "none";
  }
}
